import React, { useContext, useState } from 'react'

import { Confirm, Modal, Button, Dropdown, Image, DropdownItemProps, Loader } from 'semantic-ui-react'

import threeDotsIcon from '../../../_images/3-dots.svg'
import Data from '../../../api/Data'
import { IEpisode } from '../../../api/Data'
import { GlobalContext } from '../../../contexts/GlobalContext'
import { downloadFileFromBrowser } from '../../../utils/sharedFunctions'
import { downloadFreshSignedMedia } from '../../../utils/signedMedia'
import useFreshMediaUrl from '../../../utils/useFreshMediaUrl'

const EpisodeModal = ({ episode, setEpisodeModal, deleteEpisode }: IProps) => {
  const { isMobile, dispatchGlobalContext } = useContext(GlobalContext)

  // States
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const requestPlaybackUrl = React.useCallback(() => Data.downloadEpisode(episode.id, '480'), [episode.id])
  const { mediaUrl, isMediaLoading, mediaError, videoRef, handleMediaError, handleMediaLoaded, retryMedia } =
    useFreshMediaUrl(episode.id, requestPlaybackUrl)

  const handleDownloadEpisode = async (e: any, { value }: DropdownItemProps) => {
    const type = String(value)

    setIsDownloading(true)

    try {
      const download = await downloadFreshSignedMedia(() => Data.downloadEpisode(episode.id, type))

      downloadFileFromBrowser({ data: download.data, type: 'mp4', fileName: download.fileName })
    } catch (error) {
      if (error?.response?.status === 403) {
        setIsDownloading(false)
        return dispatchGlobalContext({ type: 'show update plan modal' })
      }
      alert('An error occurred')
    }

    return setIsDownloading(false)
  }

  const handleDelete = () => setShowConfirmationModal(true)

  return (
    <Modal dimmer="blurring" size={isMobile ? 'fullscreen' : 'small'} open onClose={() => setEpisodeModal(null)}>
      {isMediaLoading && <Loader active inline="centered" content="Loading video..." />}
      {mediaError && !isMediaLoading && <Button icon="refresh" content="Reload video" onClick={retryMedia} />}
      {mediaUrl && !isMediaLoading && !mediaError && (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          key={mediaUrl}
          ref={videoRef}
          controls
          autoPlay
          tabIndex={-1}
          onError={handleMediaError}
          onLoadedMetadata={handleMediaLoaded}
          style={{ width: '100%', height: 'auto' }}
        >
          <source src={mediaUrl} />
        </video>
      )}
      <Modal.Actions>
        <Button onClick={() => setEpisodeModal(null)}>Close</Button>

        <Dropdown text="Download video" button className="secondary" loading={isDownloading}>
          <Dropdown.Menu>
            <Dropdown.Item key="HQ" text="HD video (1920x1080)" value="1080" onClick={handleDownloadEpisode} />
            <Dropdown.Item key="MQ" text="SD video (854x480)" value="480" onClick={handleDownloadEpisode} />
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown
          style={{ display: '-webkit-inline-box' }} // Needed to align correctly
          pointing="bottom right"
          icon={<Image style={{ width: '1.5rem' }} src={threeDotsIcon} />}
        >
          <Dropdown.Menu>
            <Dropdown.Item text="Delete story" onClick={handleDelete} />
          </Dropdown.Menu>
        </Dropdown>
      </Modal.Actions>
      <Confirm
        open={showConfirmationModal}
        header="Stories deletion"
        content="Are you sure you want to delete this episode?"
        confirmButton="Yes"
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={() => deleteEpisode(episode.id)}
      />
    </Modal>
  )
}

interface IProps {
  episode: IEpisode
  setEpisodeModal: (arg: IEpisode | null) => void
  deleteEpisode: (arg: number) => void
}

export default EpisodeModal
